import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { playersAPI, API_URL } from '../services/api';
import './AdminPlayerEvaluations.css';

interface PlayerData {
  player: any;
  teams: any[];
  rankings: any[];
  evaluations: any[];
}

const AdminPlayerEvaluations: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<PlayerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'rankings' | 'evaluations'>('evaluations');

  useEffect(() => {
    if (!playerId) {
      setError('Player ID not found');
      setLoading(false);
      return;
    }

    loadPlayerData();
  }, [playerId]);

  const loadPlayerData = async () => {
    try {
      setLoading(true);
      const response = await playersAPI.getDashboard(playerId!);
      setDashboardData(response.data);
      setError('');
    } catch (err: any) {
      console.error('Failed to load player data:', err);
      setError(err.response?.data?.message || 'Failed to load player data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-player-evaluations">
        <div className="loading">Loading player data...</div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="admin-player-evaluations">
        <div className="error-message">{error || 'Failed to load player data'}</div>
        <button onClick={() => navigate('/rankings')} className="btn-back">
          ← Back to Rankings
        </button>
      </div>
    );
  }

  const { player, rankings, evaluations } = dashboardData;

  return (
    <div className="admin-player-evaluations">
      <div className="page-header">
        <button onClick={() => navigate('/rankings')} className="btn-back">
          ← Back to Rankings
        </button>
        <div className="player-header-info">
          <div className="player-photo-small">
            {player.photoUrl ? (
              <img 
                src={`${API_URL}${player.photoUrl}`} 
                alt={`${player.firstName} ${player.lastName}`}
              />
            ) : (
              <div className="photo-placeholder">👤</div>
            )}
          </div>
          <div className="player-details">
            <h1>{player.firstName} {player.lastName}</h1>
            <div className="player-meta">
              {player.position && <span className="badge">Position: {player.position}</span>}
              {player.jerseyNumber && <span className="badge">Jersey #: {player.jerseyNumber}</span>}
              {player.team && <span className="badge">Team: {player.team.name}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <button 
          className={activeTab === 'rankings' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('rankings')}
        >
          Rankings Summary
        </button>
        <button 
          className={activeTab === 'evaluations' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('evaluations')}
        >
          Detailed Evaluations
        </button>
      </div>

      <div className="content-container">
        {activeTab === 'rankings' && (
          <div className="rankings-section">
            <h2>Rankings Summary</h2>
            {rankings.length === 0 ? (
              <p className="no-data">No ranking data available.</p>
            ) : (
              <div className="rankings-summary">
                {rankings.map((ranking: any, index: number) => (
                  <div key={index} className="ranking-cards">
                    <div className="ranking-card overall">
                      <div className="rank-badge-large">#{ranking.overallRank}</div>
                      <div className="rank-info">
                        <h3>Overall Ranking</h3>
                        <p>Total Score: {ranking.overallScore}</p>
                        <p className="total">out of {ranking.totalPlayers} players</p>
                      </div>
                    </div>
                    
                    <div className="ranking-card physical">
                      <div className="rank-badge-large">#{ranking.physicalRank || 'N/A'}</div>
                      <div className="rank-info">
                        <h3>Physical Ranking</h3>
                        <p>Total Score: {ranking.physicalScore || 'N/A'}</p>
                        <p className="total">out of {ranking.totalPlayers} players</p>
                      </div>
                    </div>
                    
                    <div className="ranking-card technical">
                      <div className="rank-badge-large">#{ranking.technicalRank || 'N/A'}</div>
                      <div className="rank-info">
                        <h3>Technical Ranking</h3>
                        <p>Total Score: {ranking.technicalScore || 'N/A'}</p>
                        <p className="total">out of {ranking.totalPlayers} players</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'evaluations' && (
          <div className="evaluations-section">
            <h2>Detailed Evaluations</h2>
            <p className="info-text">Individual metrics and rankings across all evaluation categories</p>
            {evaluations.length === 0 ? (
              <p className="no-data">No evaluation data available.</p>
            ) : (
              <div className="evaluations-grid">
                {[...evaluations].sort((a, b) => {
                  const aName = a.categoryName || '';
                  const bName = b.categoryName || '';
                  
                  // 1st Touch Ball Launcher always first
                  if (aName.includes('1st Touch')) return -1;
                  if (bName.includes('1st Touch')) return 1;
                  
                  // Then other number-prefixed categories
                  const aStartsWithNumber = /^\d/.test(aName);
                  const bStartsWithNumber = /^\d/.test(bName);
                  if (aStartsWithNumber && !bStartsWithNumber) return -1;
                  if (!aStartsWithNumber && bStartsWithNumber) return 1;
                  
                  // Finally alphabetical
                  return aName.localeCompare(bName);
                }).map((evaluation: any, index: number) => (
                  <div key={index} className="evaluation-card">
                    <h3>{evaluation.categoryName}</h3>
                    <p className="evaluation-description">{evaluation.categoryDescription}</p>
                    
                    <div className="evaluation-metrics">
                      {evaluation.data && Object.keys(evaluation.data).length > 0 && (
                        <>
                          {Object.keys(evaluation.data).map((key) => {
                            // Skip if it's a rank field (we'll show it with its value)
                            if (key.includes('Rank')) return null;
                            
                            // The rank key is simply the metric name + " Rank"
                            const rankKey = key + ' Rank';
                            const rankValue = evaluation.data[rankKey];
                            
                            return (
                              <div key={key} className="metric-row">
                                <div className="metric-item">
                                  <label>{key}:</label>
                                  <span className="value">{evaluation.data[key]}</span>
                                </div>
                                {rankValue && (
                                  <div className="metric-item rank-item">
                                    <label>Rank:</label>
                                    <span className="rank">#{rankValue}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                    
                    <div className="evaluation-date">
                      Evaluated: {new Date(evaluation.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPlayerEvaluations;
